import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getSharedDocuments, GetSharedDocumentsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, Download, FolderOpen, Upload, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { isOwnerOrAdmin } from '../lib/roles';

type Doc = GetSharedDocumentsOutputType['documents'][0];

const VIS_COLORS: Record<string, string> = {
  'All Staff': 'bg-primary/10 text-primary',
  'By Department': 'bg-accent/50 text-accent-foreground',
  'By Role': 'bg-secondary/50 text-secondary-foreground',
  'Individual': 'bg-muted text-muted-foreground',
};

export default function SharedDocuments() {
  const { user } = useAuth();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [filtered, setFiltered] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const admin = isOwnerOrAdmin(user?.role);
  const isManager = admin || user?.role === 'Manager' || user?.role === 'Supervisor';

  useEffect(() => {
    if (!user) return;
    getSharedDocuments({}).then(d => {
      setDocs(d.documents);
      setFiltered(d.documents);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(!q ? docs : docs.filter(d =>
      d.title.toLowerCase().includes(q) || d.documentType.toLowerCase().includes(q),
    ));
  }, [search, docs]);

  const fmtDate = (d: string) => {
    try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; }
  };

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto space-y-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background px-4 md:px-8 py-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">Shared Documents</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Company documents shared with you by management</p>
        </div>
        {admin && (
          <Link to="/admin">
            <Button size="sm" variant="outline" className="gap-1.5">
              <Upload className="w-3.5 h-3.5" /> Manage in Admin
            </Button>
          </Link>
        )}
      </div>

      {docs.length > 0 && (
        <Input
          placeholder="Search documents..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm mb-4"
        />
      )}

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-16 px-6 text-center">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
          {docs.length === 0 ? (
            <>
              <p className="text-sm font-medium text-foreground mb-1">No documents shared yet</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                {isManager
                  ? 'Shared documents are managed in Admin → Documents tab. Upload documents there and set visibility to share them with staff.'
                  : 'Your manager or admin will share relevant documents here. Check back later or visit the Document Library for general company resources.'}
              </p>
              <div className="flex items-center justify-center gap-3">
                {admin && (
                  <Link to="/admin">
                    <Button size="sm" className="gap-1.5">
                      <Upload className="w-3.5 h-3.5" /> Upload Documents
                    </Button>
                  </Link>
                )}
                <Link to="/document-library">
                  <Button size="sm" variant="outline" className="gap-1.5">
                    Document Library <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No documents match your search.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(doc => (
            <div key={doc.id} className="flex items-center gap-3 p-4 border border-border rounded-lg bg-card hover:border-primary/30 transition-colors">
              <FileText className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-muted-foreground">{doc.documentType}</span>
                  {doc.uploadDate && <span className="text-xs text-muted-foreground">· {fmtDate(doc.uploadDate)}</span>}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${VIS_COLORS[doc.visibility] || 'bg-muted text-muted-foreground'}`}>
                    {doc.visibility}
                  </span>
                </div>
              </div>
              {doc.fileUrl && (
                <a href={doc.fileUrl} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0"><Download className="w-4 h-4" /></Button>
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Footer context */}
      <div className="mt-8 text-xs text-muted-foreground border-t border-border pt-4 space-y-1">
        <p>Showing {filtered.length} document{filtered.length !== 1 ? 's' : ''} visible to you based on your role ({user?.role}) and department ({user?.department || 'unset'}).</p>
        <p>Documents are managed by Admin via the <Link to="/document-library" className="text-primary underline">Document Library</Link> or the Admin panel.</p>
      </div>
    </div>
  );
}
